import React, { useEffect, useState, useMemo } from 'react';
import { getContracts, getFinancials, getAopTargets } from '../../services/api';
import bHuman from '../../assets/values/bHuman.png';
import bFast from '../../assets/values/bFast.png';
import bPrecise from '../../assets/values/bPrecise.png';
import bRelentless from '../../assets/values/bRelentless.png';
import bSlashLogo from '../../assets/values/bSlashLogo.png';

const MONTH_KEYS = ['2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06', '2026-07', '2026-08', '2026-09', '2026-10', '2026-11', '2026-12'];
const DEAL_TYPES = ['BACKLOG', 'RECOMPETE', 'NB_OG', 'NEW_BIZ'];

function formatCurrency(value) {
  if (value === null || value === undefined) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCompactCurrency(value) {
  if (value === null || value === undefined) return '--';
  if (Math.abs(value) >= 1000000) return '$' + (value / 1000000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1000) return '$' + (value / 1000).toFixed(0) + 'K';
  return '$' + value.toLocaleString();
}

const CORE_VALUES = [
  { title: 'Human', image: bHuman, description: 'Mission-tempered engineers, analysts, and technologists\u2014operating as a single team of teams\u2014turn hard problems into solutions.' },
  { title: 'Fast', image: bFast, description: 'Speed is our tactic\u2014iterate, learn, and field faster than the threat cycle.' },
  { title: 'Precise', image: bPrecise, description: 'From code to strategy, we zero in on what matters and eliminate noise. Clarity leads. Accuracy follows.' },
  { title: 'Relentless', image: bRelentless, description: 'Hard problems fuel us\u2014adapt, re-attack, and finish. Quitting isn\u2019t in the playbook.' },
];

const NEWS_ITEMS = [
  { title: 'Mission Possible Podcast: Joseph Stefano on Strategic Leadership and Driving Outcomes', url: 'https://bcore.com/news-insights/mission-possible-podcast-joseph-stefano-on-strategic-leadership-and-driving-outcomes/', tag: 'Podcast' },
  { title: 'Mission Possible Podcast: Vice Admiral Bob Sharp, USN (Ret.) on Leading Through Complexity', url: 'https://bcore.com/news-insights/mission-possible-podcast-vice-admiral-bob-sharp-usn-ret-on-leading-through-complexity/', tag: 'Podcast' },
  { title: 'Mission Possible Podcast: John Quick on Leadership, Adversity, and Living with Intention', url: 'https://bcore.com/news-insights/mission-possible-podcast-john-quick-on-leadership-adversity-and-living-with-intention/', tag: 'Podcast' },
  { title: 'Navigating Entrepreneurship, Trust, and Culture: Tyler Laverick on the Mission Possible Podcast', url: 'https://bcore.com/news-insights/navigating-entrepreneurship-trust-and-culture-tyler-laverick-on-the-mission-possible-podcast/', tag: 'Podcast' },
  { title: 'Mission Possible Podcast: Vinita Fordham on Technology, Trust, and Transformation', url: 'https://bcore.com/news-insights/mission-possible-podcast-vinita-fordham-on-technology-trust-and-transformation/', tag: 'Podcast' },
  { title: 'Bcore Unveils Unified Brand to Power the Nation\u2019s Most Complex Missions', url: 'https://bcore.com/news-insights/bcore-unveils-unified-brand-to-power-the-nations-most-complex-missions/', tag: 'Press Release' },
];

function Dashboard() {
  const [contracts, setContracts] = useState([]);
  const [financials, setFinancials] = useState(null);
  const [aopTargets, setAopTargets] = useState(null);

  // Fetch contracts and financial data
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [contractData, finData, aopData] = await Promise.all([
          getContracts(),
          getFinancials('2026', 'All').catch(() => null),
          getAopTargets('2026', 'All').catch(() => null),
        ]);
        if (!cancelled) {
          setContracts(contractData);
          setFinancials(finData);
          setAopTargets(aopData);
        }
      } catch {
        // Dashboard still renders with empty data
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Compute financial KPIs
  const kpis = useMemo(() => {
    const empty = {
      totalForecast: 0, gapToAop: 0,
      backlog: 0, backlogPct: 0,
      recompete: 0, recompetePct: 0,
      ytdActuals: 0, actualMonths: 0,
    };
    if (!financials?.divisions) return empty;

    const divs = financials.divisions;
    const divKeys = Object.keys(divs);
    if (divKeys.length === 0) return empty;

    // Aggregate all divisions
    const byDealType = {};
    DEAL_TYPES.forEach((dt) => { byDealType[dt] = new Array(12).fill(0); });
    const monthlyTotals = new Array(12).fill(0);
    let lastActualIdx = -1;

    divKeys.forEach((dk) => {
      const d = divs[dk];
      if (d.by_deal_type) {
        DEAL_TYPES.forEach((dt) => {
          if (!d.by_deal_type[dt]) return;
          MONTH_KEYS.forEach((mk, i) => {
            const entry = d.by_deal_type[dt][mk];
            if (entry) {
              byDealType[dt][i] += entry.revenue || 0;
              if (entry.is_actual && i > lastActualIdx) lastActualIdx = i;
            }
          });
        });
      }
    });

    DEAL_TYPES.forEach((dt) => {
      byDealType[dt].forEach((val, i) => { monthlyTotals[i] += val; });
    });

    const totalForecast = monthlyTotals.reduce((a, b) => a + b, 0);

    // AOP
    let totalAop = 0;
    if (aopTargets?.targets) {
      const targets = aopTargets.targets;
      Object.keys(targets).forEach((dk) => {
        DEAL_TYPES.forEach((dt) => {
          if (!targets[dk][dt]) return;
          MONTH_KEYS.forEach((mk) => {
            const val = targets[dk][dt][mk];
            if (val !== undefined && val !== null) totalAop += val;
          });
        });
      });
    }

    const gapToAop = totalForecast - totalAop;
    const backlog = byDealType.BACKLOG.reduce((a, b) => a + b, 0);
    const recompete = byDealType.RECOMPETE.reduce((a, b) => a + b, 0);
    const backlogPct = totalForecast > 0 ? Math.round((backlog / totalForecast) * 100) : 0;
    const recompetePct = totalForecast > 0 ? Math.round((recompete / totalForecast) * 100) : 0;

    let ytdActuals = 0;
    let actualMonths = 0;
    monthlyTotals.forEach((val, i) => {
      if (i <= lastActualIdx) {
        ytdActuals += val;
        actualMonths++;
      }
    });

    return { totalForecast, gapToAop, backlog, backlogPct, recompete, recompetePct, ytdActuals, actualMonths };
  }, [financials, aopTargets]);

  return (
    <div>
      {/* Financial KPI Tiles */}
      <div className="forecast-kpi-row">
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Total Forecast</span>
            <div className="stat-card-icon accent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.totalForecast)}</div>
          <div className="stat-card-sub">
            <span className={kpis.gapToAop >= 0 ? 'up' : 'down'}>
              {kpis.gapToAop >= 0 ? '+' : ''}{formatCompactCurrency(kpis.gapToAop)}
            </span> vs AOP
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Backlog Revenue</span>
            <div className="stat-card-icon blue">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.backlog)}</div>
          <div className="stat-card-sub">{kpis.backlogPct}% of total forecast</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Recompete Revenue</span>
            <div className="stat-card-icon green">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.recompete)}</div>
          <div className="stat-card-sub">{kpis.recompetePct}% of total forecast</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">YTD Actuals</span>
            <div className="stat-card-icon accent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.ytdActuals)}</div>
          <div className="stat-card-sub">{kpis.actualMonths} month{kpis.actualMonths !== 1 ? 's' : ''} closed</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Active Contracts</span>
            <div className="stat-card-icon blue">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{contracts.filter((c) => c.status === 'Active').length}</div>
          <div className="stat-card-sub">{contracts.length} total contracts</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Active Personnel</span>
            <div className="stat-card-icon green">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
            </div>
          </div>
          <div className="stat-card-value">14</div>
          <div className="stat-card-sub"><span className="up">+2</span> from last month</div>
        </div>
      </div>

      {/* Section: Core Values */}
      <div className="dash-section-divider">
        <div className="dash-section-line" />
        <span className="dash-section-label">Core Values</span>
        <div className="dash-section-line" />
      </div>

      <div className="core-values-section">
        {CORE_VALUES.map((val) => (
          <div className="core-value-item" key={val.title}>
            <img className="core-value-image" src={val.image} alt={val.title} />
            <div className="core-value-label">
              <img className="core-value-slash" src={bSlashLogo} alt="" />
              <span className="core-value-title">{val.title}</span>
            </div>
            <div className="core-value-description">{val.description}</div>
          </div>
        ))}
      </div>

      {/* Section: News & Press */}
      <div className="dash-section-divider">
        <div className="dash-section-line" />
        <span className="dash-section-label">News &amp; Press</span>
        <div className="dash-section-line" />
      </div>

      <div className="dash-news-section">
        {NEWS_ITEMS.map((item) => (
          <a
            className="dash-news-item"
            key={item.url}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="dash-news-item-inner">
              <span className="dash-news-tag">{item.tag}</span>
              <span className="dash-news-title">{item.title}</span>
            </div>
            <svg className="dash-news-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        ))}
        <a
          className="dash-news-viewall"
          href="https://bcore.com/news-insights/"
          target="_blank"
          rel="noopener noreferrer"
        >
          View All on bcore.com
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      </div>
    </div>
  );
}

export default Dashboard;
